/**
 * Starea PhotoPicker-ului ca funcție pură, ca să fie testabilă fără DOM
 * (vitest rulează cu environment: 'node').
 */
import { esteUrlPdf, numeFisierDinUrl } from '@/shared/utils/este-url-pdf';

export interface PickedPhoto {
  localId: string;
  /** Lipsește la pozele hidratate dintr-un răspuns salvat (avem doar URL-ul). */
  file?: File;
  previewUrl: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  progress: number;
  fileId?: string;
  error?: string;
}

export type PhotoAction =
  | { type: 'add'; photos: PickedPhoto[]; max: number }
  | { type: 'progress'; localId: string; progress: number }
  | { type: 'done'; localId: string; fileId: string }
  | { type: 'error'; localId: string; error: string }
  | { type: 'remove'; localId: string };

function update(state: PickedPhoto[], localId: string, patch: Partial<PickedPhoto>): PickedPhoto[] {
  const index = state.findIndex(p => p.localId === localId);
  if (index === -1) return state;
  const next = state.slice();
  next[index] = { ...state[index], ...patch };
  return next;
}

/** I3 (final-review.md): dupa upload, `fileId` e in practica URL-ul remote al fotografiei
 *  (vezi ImageUploadCompletion.tsx — hidrateaza `previewUrl: url, fileId: url`). Cand e asa,
 *  tranzitia `done` schimba si `previewUrl` pe el, ca miniatura sa ramana utilizabila dupa ce
 *  PhotoPicker revoca URL-ul blob original. */
function looksLikeUrl(value: string): boolean {
  return /^(https?:|data:)/i.test(value);
}

/** Task 15 (B3): o dovada PDF — dupa fisierul local, sau, la una hidratata/urcata, dupa URL.
 *  Un PDF nu se randeaza niciodata ca `<img>`. */
export function esteDovadaPdf(p: PickedPhoto): boolean {
  if (p.file) return p.file.type === 'application/pdf';
  return esteUrlPdf(p.fileId) || esteUrlPdf(p.previewUrl);
}

/** Numele afisat pe tile-ul unui PDF: al fisierului ales, altfel ultimul segment din URL. */
export function numeDovada(p: PickedPhoto): string {
  return p.file?.name || numeFisierDinUrl(p.fileId || p.previewUrl);
}

export function photoPickerReducer(state: PickedPhoto[], action: PhotoAction): PickedPhoto[] {
  switch (action.type) {
    case 'add': {
      const known = new Set(state.map(p => p.localId));
      const fresh = action.photos.filter(p => !known.has(p.localId));
      const room = Math.max(0, action.max - state.length);
      return room === 0 ? state : [...state, ...fresh.slice(0, room)];
    }
    case 'progress':
      return update(state, action.localId, { status: 'uploading', progress: Math.min(99, Math.max(0, action.progress)) });
    case 'done': {
      const patch: Partial<PickedPhoto> = { status: 'done', progress: 100, fileId: action.fileId, error: undefined };
      // Task 15: un PDF urcat nu devine sursa unei miniaturi (ar iesi imagine sparta in `<img>`).
      if (looksLikeUrl(action.fileId) && !esteUrlPdf(action.fileId)) patch.previewUrl = action.fileId;
      return update(state, action.localId, patch);
    }
    case 'error':
      return update(state, action.localId, { status: 'error', error: action.error });
    case 'remove':
      return state.some(p => p.localId === action.localId) ? state.filter(p => p.localId !== action.localId) : state;
  }
}
